import {
  CLINICIAN_ROLES,
  IAdvanceSearchFilter,
  IBroadcastInfoMembers,
  IDepartmentData,
  IGroupInfoMembers,
  INearbyPatient,
} from "@home-health-notify/domain";
import {
  AddMembersContext,
  buildClinicianRoleFilters,
  DISTANCE_IN_MILES,
  IClinicianFilterType,
  IAddMembersContext,
  IOrganizationContext,
  OrganizationContext,
  useAdvancedSearch,
  useTranslation,
} from "hhn-commons";
import { ReactNode, useContext, useEffect, useState } from "react";
import { Dialog } from "../modal/Dialog";
import AdvanceSearchMap from "../../addMember/addUsers/advanceSearch/map/AdvanceSearchMap";
import {
  AdvanceSearchContainer,
  ClinicianListContainer,
  PatientHeaderContainer,
} from "../../addMember/addUsers/advanceSearch/list/AdvanceSearchList.style";
import CommonDrawerHeader from "../CommonDrawerHeader";
import { DrawerDivider } from "../../layout/RightDrawer.style";
import ClinicianSearchResults from "../../addMember/addUsers/advanceSearch/list/ClinicianSearchResults";
import {
  EmptyStateContainer,
  EmptyStateText,
} from "../../careMap/CareMapDialog.style";
import PersonPinCircleIcon from "@mui/icons-material/PersonPinCircle";
import { DEFAULT_MAP_POSITION } from "../../addPatientGroup/addressField/AddressMapHelper";

interface ClinicianMapDialogProps {
  open?: boolean;
  closeModal: () => void;
  title: string;
  testId: string;

  advanceSearchFilters?: IAdvanceSearchFilter;

  headerContent: ReactNode;
  centerPinContent?: ReactNode;

  disciplinesFilters?: IClinicianFilterType[];
  initialDiscipline?: CLINICIAN_ROLES;
  initialDistance?: DISTANCE_IN_MILES;
  defaultZoom?: number;

  showActions?: boolean;
  existingMembers?: IGroupInfoMembers[] | IBroadcastInfoMembers[];
  existingGroup?: boolean;
  enableReferrals?: boolean;
  onAddMemberCallback?: (selectedMembers: any) => void;
}

export const ClinicianMapDialog = ({
  open = true,
  closeModal,
  title,
  testId,
  advanceSearchFilters,
  headerContent,
  centerPinContent,
  disciplinesFilters: disciplinesFiltersProp,
  initialDiscipline = CLINICIAN_ROLES.SN,
  initialDistance = DISTANCE_IN_MILES.TEN,
  defaultZoom,
  showActions = false,
  existingMembers,
  existingGroup,
  enableReferrals,
  onAddMemberCallback,
}: ClinicianMapDialogProps) => {
  const { t } = useTranslation();
  const tNAdvanceSearch = (key: string) => t(key, { ns: "advanceSearch" });

  const { selectedOrgUser }: IOrganizationContext =
    useContext(OrganizationContext);
  const { onAddMembers }: IAddMembersContext = useContext(AddMembersContext);

  const chatManager = selectedOrgUser?.chatManager.getPatientChatManager();
  const departments: IDepartmentData[] =
    selectedOrgUser?.getDepartments() || [];

  const defaultDisciplinesFilters = buildClinicianRoleFilters(departments);
  const disciplinesFilters =
    disciplinesFiltersProp || defaultDisciplinesFilters;

  const [selectedDiscipline, setSelectedDiscipline] =
    useState<CLINICIAN_ROLES>(initialDiscipline);
  const [selectedDistance, setSelectedDistance] =
    useState<DISTANCE_IN_MILES>(initialDistance);
  const [filteredNearbyPatients, setFilteredNearbyPatients] = useState<
    INearbyPatient[] | undefined
  >(undefined);
  const [isAddMembersLoading, setIsAddMembersLoading] = useState(false);

  const hasSearchData = advanceSearchFilters !== undefined;

  const { isLoading, fetchData, resetData } = useAdvancedSearch({
    chatManager,
    advanceSearchFilters,
    selectedDistance,
    selectedDiscipline,
    departments,
  });

  const onSelectDiscipline = (discipline: CLINICIAN_ROLES) => {
    resetData();
    setSelectedDiscipline(discipline);
  };

  const onSelectDistance = (distance: DISTANCE_IN_MILES) => {
    resetData();
    setSelectedDistance(distance);
  };

  useEffect(() => {
    if (open && hasSearchData) {
      fetchData();
    }
  }, [open, hasSearchData, fetchData]);

  const addMembersAndCloseDialog = async () => {
    if (!onAddMemberCallback) return;
    setIsAddMembersLoading(true);
    await onAddMembers(onAddMemberCallback);
    setIsAddMembersLoading(false);
    closeModal();
  };

  const nearbyPatients =
    chatManager?.advanceSearchClinicianList?.nearbyPatients;
  const users = chatManager?.advanceSearchClinicianList?.users || [];

  return (
    <Dialog fullScreen open={open} onClose={closeModal} data-testid={testId}>
      <AdvanceSearchContainer>
        <AdvanceSearchMap
          nearbyPatients={hasSearchData ? filteredNearbyPatients : undefined}
          advanceSearchFilters={
            advanceSearchFilters || {
              lat: DEFAULT_MAP_POSITION.lat,
              lng: DEFAULT_MAP_POSITION.lng,
            }
          }
          isLoading={isLoading}
          centerPinContent={hasSearchData ? centerPinContent : undefined}
          defaultZoom={hasSearchData ? undefined : defaultZoom}
          selectedDistance={selectedDistance}
          selectedDiscipline={selectedDiscipline}
        />
        <ClinicianListContainer>
          <CommonDrawerHeader
            showBackButton={false}
            title={tNAdvanceSearch(title)}
            onCloseButtonClick={closeModal}
          />
          <PatientHeaderContainer>{headerContent}</PatientHeaderContainer>
          <DrawerDivider />
          {hasSearchData ? (
            <ClinicianSearchResults
              onSelectDiscipline={onSelectDiscipline}
              selectedDiscipline={selectedDiscipline}
              disciplinesFilters={disciplinesFilters}
              onSelectDistance={onSelectDistance}
              selectedDistance={selectedDistance}
              isLoading={isLoading}
              users={users}
              patientsCount={nearbyPatients?.length ?? 0}
              onScrollCallback={fetchData}
              showActions={showActions}
              enableReferrals={enableReferrals}
              existingMembers={existingMembers}
              existingGroup={existingGroup}
              onAddMemberCallback={onAddMemberCallback}
              isAddMembersLoading={isAddMembersLoading}
              addMembersAndCloseDialog={addMembersAndCloseDialog}
              nearbyPatients={nearbyPatients}
              onFilteredPatientsChange={setFilteredNearbyPatients}
            />
          ) : (
            <EmptyStateContainer>
              <PersonPinCircleIcon sx={{ fontSize: 48, color: "grey.400" }} />
              <EmptyStateText>
                {tNAdvanceSearch("searchToSeeResults")}
              </EmptyStateText>
            </EmptyStateContainer>
          )}
        </ClinicianListContainer>
      </AdvanceSearchContainer>
    </Dialog>
  );
};
